import React, { useState } from "react";
import _isArray from "lodash/isArray";
import {
  ModelActionEnum,
  detailService,
} from "services/page-services/detail-service";
import { Responsibility } from "models/Responsibility";
import { webService } from "services/common-services/web-service";
import { fieldService } from "services/page-services/field-service";
import appMessageService from "services/common-services/app-message-service";
import {
  FormItem,
  LayoutCell,
  LayoutHeader,
  MultipleSelect,
  OneLineText,
  StatusLine,
} from "react3l-ui-library";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import nameof from "ts-nameof.macro";
import { ColumnProps } from "antd/lib/table";
import { Popconfirm } from "antd";
import { TrashCan16 } from "@carbon/icons-react";
import { responsibilityRepository } from "repositories/responsibility-repository";
import {
  ResponsibilityRoleMapping,
  ResponsibilityRoleMappingFilter,
} from "models/ResponsibilityRoleMapping";
import { masterTableIndex } from "helpers/table";
import { Role } from "models/Role";
import { RESPONSIBILITY_ROUTE } from "config/route-consts";
import { v4 as uuidv4 } from "uuid";
import {
  ResponsibilityAppUserGroupingMappings,
  ResponsibilityAppUserGroupingMappingsFilter,
} from "models/ResponsibilityAppUserGroupingMappings";
import _cloneDeep from "lodash/cloneDeep";
import { AppUserGrouping } from "models/AppUserGrouping";
import _ from "lodash";
import { Organization, OrganizationFilter } from "models/Organization";
import { utilService } from "services/common-services/util-service";
import TreeSelect from "components/TreeSelect";
import { Province, ProvinceFilter } from "models/Province";

export default function useResponsibilityDetail() {
  const [translate] = useTranslation();
  const [isActive, setIsActive] = useState(false);

  const { model, dispatch } = detailService.useModel<Responsibility>(
    Responsibility,
    {
      ...new Responsibility(),
      statusId: 1,
      status: {
        id: 1,
        name: "Hoạt động",
        color: "#24a148",
      },
    }
  );

  const { isDetail } = detailService.useGetIsDetail(
    responsibilityRepository.get,
    dispatch
  );

  const {
    handleChangeSingleField,
    handleChangeAllField,
    handleChangeSelectField,
    handleChangeListField,
  } = fieldService.useField(model, dispatch);

  const { notifyUpdateItemSuccess, notifyUpdateItemError } =
    appMessageService.useCRUDMessage();

  const [subscription] = webService.useSubscription();
  const [isLoading, setIsLoading] = useState(false);

  const { handleGoMaster } = detailService.useActionsDetail<Responsibility>(
    model,
    responsibilityRepository.save,
    handleChangeAllField,
    RESPONSIBILITY_ROUTE
  );

  const handleChangeStatus = React.useCallback(
    (checked) => {
      const newModel = { ...model };
      if (checked) {
        newModel.statusId = 1;
        setIsActive(false);
      } else {
        newModel.statusId = 0;
        setIsActive(true);
      }
      handleChangeAllField(newModel);
    },
    [handleChangeAllField, model]
  );

  const handleChangeIsTendering = React.useCallback(
    (checked) => {
      const newModel = { ...model };
      newModel.isTendering = checked;
      handleChangeAllField(newModel);
    },
    [handleChangeAllField, model]
  );

  const handleSaveResponsibility = React.useCallback(() => {
    setIsLoading(true);
    subscription.add(
      responsibilityRepository.save(model).subscribe({
        next: (item: Responsibility) => {
          handleChangeAllField(item); // setModel
          notifyUpdateItemSuccess();
          handleGoMaster(); // go master
          setIsLoading(false);
        },
        error: (error: AxiosError<Responsibility>) => {
          if (error.response && error.response.status === 400) {
            handleChangeAllField(error.response?.data);
          }
          if (
            error.response &&
            error.response.status === 400 &&
            error.response.data.errors?.id
          ) {
            notifyUpdateItemError({
              message: String(error.response.data.errors?.id),
            });
          }
          setIsLoading(false);
        },
      })
    );
  }, [
    handleChangeAllField,
    handleGoMaster,
    model,
    notifyUpdateItemError,
    notifyUpdateItemSuccess,
    subscription,
  ]);

  const responsibilityRoleMappings = React.useMemo<any[]>(() => {
    if (_isArray(model["responsibilityRoleMappings"])) {
      const newData = model["responsibilityRoleMappings"].map(
        (current: ResponsibilityRoleMapping) => {
          if (typeof current.key !== "undefined") {
            return current;
          } else {
            current.key = uuidv4();
            return current;
          }
        }
      );
      return newData;
    } else {
      return [];
    }
  }, [model]);

  const handleDeleteResponsibilityRole = React.useCallback(
    (index: number) => (event: any) => {
      responsibilityRoleMappings.splice(index, 1);
      handleChangeListField({
        fieldName: "responsibilityRoleMappings",
      })(responsibilityRoleMappings);
    },
    [handleChangeListField, responsibilityRoleMappings]
  );

  const handleDeleteGroupUsers = React.useCallback(
    (item: any) => {
      const modelHere = _cloneDeep(model);
      const listAppUserId = model.responsibilityAppUserGroupingMappings.map(
        (item: any) => item.appUserGroupingId
      );
      modelHere.responsibilityAppUserGroupingMappings.splice(
        listAppUserId?.indexOf(item?.appUserGroupingId),
        1
      );
      dispatch({
        type: ModelActionEnum.UPDATE,
        payload: modelHere,
      });
    },
    [model, dispatch]
  );

  const handleChangeMultipleSelect = React.useCallback(
    (fieldName: string, index: number) => (values: Organization[]) => {
      const responsibilityAppUserGroupingMappings = _.cloneDeep(
        model?.responsibilityAppUserGroupingMappings
      );
      responsibilityAppUserGroupingMappings[index][`${fieldName}`] = values;
      handleChangeListField({
        fieldName: "responsibilityAppUserGroupingMappings",
      })(responsibilityAppUserGroupingMappings);
    },
    [handleChangeListField, model?.responsibilityAppUserGroupingMappings]
  );


  const responsibilityColumns: ColumnProps<ResponsibilityRoleMapping>[] =
    React.useMemo(
      () => [
        {
          title: translate("general.columns.orderNumber"),
          width: 30,
          align: "center",
          key: nameof(responsibilityRoleMappings[0].key),
          dataIndex: nameof(responsibilityRoleMappings[0].key),
          render: masterTableIndex<
            ResponsibilityRoleMapping,
            ResponsibilityRoleMappingFilter
          >(new ResponsibilityRoleMappingFilter()),
        },

        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate("responsibilities.roles.code")}
              />
            );
          },
          key: nameof(responsibilityRoleMappings[0].code),
          dataIndex: nameof(responsibilityRoleMappings[0].role),
          width: 250,
          render(...params: [Role, ResponsibilityRoleMapping, number]) {
            return (
              <LayoutCell orderType="left" tableSize="md">
                <OneLineText countCharacters={50} value={params[0]?.code} />
              </LayoutCell>
            );
          },
        },

        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate("responsibilities.roles.name")}
              />
            );
          },
          key: nameof(responsibilityRoleMappings[0].name),
          dataIndex: nameof(responsibilityRoleMappings[0].role),
          width: 250,
          render(...params: [Role, ResponsibilityRoleMapping, number]) {
            return (
              <LayoutCell orderType="left" tableSize="md">
                <OneLineText countCharacters={50} value={params[0]?.name} />
              </LayoutCell>
            );
          },
        },
        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate("responsibilities.roles.site")}
              />
            );
          },
          key: nameof(responsibilityRoleMappings[0].site),
          dataIndex: nameof(responsibilityRoleMappings[0].role),
          width: 250,
          render(...params: [Role, ResponsibilityRoleMapping, number]) {
            return (
              <LayoutCell orderType="left" tableSize="md">
                <OneLineText
                  countCharacters={50}
                  value={params[0]?.site?.name}
                />
              </LayoutCell>
            );
          },
        },

        {
          key: "action",
          dataIndex: nameof(responsibilityRoleMappings[0].id),
          fixed: "right",
          width: 80,
          align: "center",
          render(...[, , index]) {
            return (
              <Popconfirm
                placement="left"
                title={translate("general.deletes.content")}
                onConfirm={handleDeleteResponsibilityRole(index)}
                okText={translate("general.actions.delete")}
                cancelText={translate("general.actions.cancel")}
              >
                <TrashCan16 color="#da1e28" />
              </Popconfirm>
            );
          },
        },
      ],
      [handleDeleteResponsibilityRole, responsibilityRoleMappings, translate]
    );

  const groupUserColumns: ColumnProps<ResponsibilityAppUserGroupingMappings>[] =
    React.useMemo(
      () => [
        {
          title: translate("general.columns.orderNumber"),
          width: 60,
          align: "center",
          key: nameof(responsibilityRoleMappings[0].key),
          dataIndex: nameof(responsibilityRoleMappings[0].key),
          render: masterTableIndex<
            ResponsibilityAppUserGroupingMappings,
            ResponsibilityAppUserGroupingMappingsFilter
          >(new ResponsibilityAppUserGroupingMappingsFilter()),
        },

        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate("responsibilities.listUserGroup.code")}
              />
            );
          },
          key: "code",
          dataIndex: "code",
          width: 200,
          render(
            ...params: [string, ResponsibilityAppUserGroupingMappings, number]
          ) {
            return (
              <LayoutCell orderType="left" tableSize="md">
                <OneLineText
                  countCharacters={50}
                  value={params[1]?.appUserGrouping?.code}
                />
              </LayoutCell>
            );
          },
        },

        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate("responsibilities.listUserGroup.name")}
              />
            );
          },
          key: "name",
          dataIndex: "name",
          width: 250,
          render(
            ...params: [string, ResponsibilityAppUserGroupingMappings, number]
          ) {
            return (
              <LayoutCell orderType="left" tableSize="md">
                <OneLineText
                  countCharacters={50}
                  value={params[1]?.appUserGrouping?.name}
                />
              </LayoutCell>
            );
          },
        },
        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate(
                  "responsibilities.listUserGroup.managementUnit"
                )}
              />
            );
          },
          key: "organization",
          dataIndex: "appUserGrouping",
          width: 250,
          render(
            ...params: [
              AppUserGrouping,
              ResponsibilityAppUserGroupingMappings,
              number
            ]
          ) {
            const listOrganizationMappings = params[0]
              ?.appUserGroupingOrganizationMappings
              ? _.cloneDeep(params[0]?.appUserGroupingOrganizationMappings)
              : [];
            const listOrganizationNames = listOrganizationMappings.map(
              (item: Organization) => item?.organization?.name
            );
            return (
              <LayoutCell orderType="left" tableSize="md">
                <OneLineText
                  countCharacters={30}
                  value={listOrganizationNames.join(", ")}
                />
              </LayoutCell>
            );
          },
        },

        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate(
                  "responsibilities.listUserGroup.organizations"
                )}
              />
            );
          },
          key: "organizations",
          dataIndex: "organizations",
          width: 300,
          render(
            ...params: [
              Organization[],
              ResponsibilityAppUserGroupingMappings,
              number
            ]
          ) {
            return (
              <LayoutCell orderType="left" tableSize="lg">
                <FormItem
                  validateStatus={utilService.getValidateStatus(
                    params[1],
                    nameof(params[1].organizations)
                  )}
                  message={params[1].errors?.organizations}
                >
                  <TreeSelect
                    isRequired
                    type={0}
                    placeHolder={translate(
                      "responsibilities.listUserGroup.placeholder.organizations"
                    )}
                    selectable={true}
                    classFilter={OrganizationFilter}
                    onChange={(values: any[]) => {
                      const internalValues =
                        values && values?.length > 0
                          ? values.map((value: Organization) => {
                              value.organizationId = value?.id;
                              value.appUserGroupingId = 0;
                              return value;
                            })
                          : [];
                      handleChangeMultipleSelect(
                        "organizations",
                        params[2]
                      )(internalValues);
                    }}
                    getTreeData={
                      responsibilityRepository.singleListOrganization
                    }
                    checkable
                    isUsingSearch
                    searchProperty="search"
                    searchType=""
                    listItem={params[0]}
                    render={(object: Organization) => {
                      return object?.code && object?.name
                        ? `${object?.code} - ${object?.name}`
                        : null;
                    }}
                    appendToBody
                  />
                </FormItem>
              </LayoutCell>
            );
          },
        },

        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate("responsibilities.listUserGroup.provinces")}
              />
            );
          },
          key: "provinces",
          dataIndex: "provinces",
          width: 300,
          render(
            ...params: [
              Province[],
              ResponsibilityAppUserGroupingMappings,
              number
            ]
          ) {
            return (
              <LayoutCell orderType="left" tableSize="lg">
                <FormItem
                  validateStatus={utilService.getValidateStatus(
                    params[1],
                    nameof(params[1].provinces)
                  )}
                  message={params[1].errors?.provinces}
                >
                  <MultipleSelect
                    isUsingSearch
                    type={0}
                    placeHolder={translate(
                      "responsibilities.listUserGroup.placeholder.provinces"
                    )}
                    onChange={handleChangeMultipleSelect(
                      "provinces",
                      params[2]
                    )}
                    getList={responsibilityRepository.singleListProvince}
                    values={params[0] || []}
                    render={(object: Province) => {
                      return object?.code && object?.name
                        ? `${object?.code} - ${object?.name}`
                        : null;
                    }}
                    classFilter={ProvinceFilter}
                    maxLengthItem={50}
                    searchType=""
                    searchProperty="search"
                    appendToBody
                  />
                </FormItem>
              </LayoutCell>
            );
          },
        },
        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate("responsibilities.listUserGroup.status")}
              />
            );
          },
          key: "status",
          dataIndex: "status",
          width: 200,
          render(
            ...params: [any, ResponsibilityAppUserGroupingMappings, number]
          ) {
            return (
              <LayoutCell orderType="left" tableSize="md">
                <StatusLine
                  value={params[1]?.appUserGrouping?.status?.name}
                  color={params[1]?.appUserGrouping?.status?.color}
                />
              </LayoutCell>
            );
          },
        },

        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate("general.actions.label")}
                className="d-flex justify-content-center"
              />
            );
          },
          key: "action",
          dataIndex: nameof(responsibilityRoleMappings[0].id),
          fixed: "right",
          width: 80,
          align: "center",
          render(
            ...params: [any, ResponsibilityAppUserGroupingMappings, number]
          ) {
            return (
              <Popconfirm
                placement="left"
                title={translate("general.deletes.content")}
                onConfirm={() => handleDeleteGroupUsers(params[1])}
                okText={translate("general.actions.delete")}
                cancelText={translate("general.actions.cancel")}
              >
                <TrashCan16 color="#da1e28" className="hover-class" />
              </Popconfirm>
            );
          },
        },
      ],
      [
        translate,
        responsibilityRoleMappings,
        handleChangeMultipleSelect,
        handleDeleteGroupUsers,
      ]
    );

  React.useEffect(() => {
    if (model.statusId === 1) {
      setIsActive(false);
    } else {
      setIsActive(true);
    }
  }, [model.statusId]);

  return {
    model,
    isDetail,
    responsibilityColumns,
    responsibilityRoleMappings,
    handleChangeSingleField,
    handleChangeSelectField,
    handleChangeListField,
    handleChangeStatus,
    handleChangeIsTendering,
    handleSaveResponsibility,
    handleGoMaster,
    isLoading,
    groupUserColumns,
    dispatch,
    isActive,
  };
}
